/** Root layout (PL-03): branded sidebar + topbar + routed Outlet. MASTER §5. */
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Dumbbell, LogOut, MapPin, Menu, Moon, Smartphone, Sun, UserRound, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useTheme } from '../app/ThemeProvider.js';
import { useAuth } from '../lib/auth.js';
import { useLocations } from '../lib/locations.js';
import { navFor, type NavGroup, type NavItem } from '../lib/navigation.js';

import { cn } from './ui/cn.js';
import { LocaleSwitcher } from './ui/LocaleSwitcher.js';
import { StatusPill } from './ui/StatusPill.js';
import { Toaster } from './ui/Toaster.js';

const GROUP_ORDER: NavGroup[] = ['operate', 'manage', 'system'];

function Brand({ collapsed }: { collapsed?: boolean }): ReactNode {
  return (
    <div className="flex h-14 items-center gap-2.5 px-4">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary text-black">
        <Dumbbell className="h-5 w-5" aria-hidden />
      </span>
      {!collapsed ? (
        <div className="leading-none">
          <span className="font-display text-lg font-bold tracking-tight text-fg">FlexGym</span>
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-muted">
            Admin
          </span>
        </div>
      ) : null}
    </div>
  );
}

function SidebarNav({
  items,
  onNavigate,
}: {
  items: NavItem[];
  onNavigate?: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      {GROUP_ORDER.map((group) => {
        const groupItems = items.filter((i) => i.group === group);
        if (groupItems.length === 0) return null;
        return (
          <div key={group} className="mb-1.5">
            <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-faint">
              {t(`nav.group.${group}`)}
            </p>
            <ul className="grid gap-0.5">
              {groupItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                          isActive
                            ? 'bg-surface-2 text-fg'
                            : 'text-muted hover:bg-surface-2/60 hover:text-fg',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            className={cn(
                              'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-primary transition-opacity',
                              isActive ? 'opacity-100' : 'opacity-0',
                            )}
                            aria-hidden
                          />
                          <Icon
                            className={cn('h-[18px] w-[18px] shrink-0', isActive && 'text-primary')}
                            aria-hidden
                          />
                          <span className="truncate">{t(item.labelKey)}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function LocationSwitcher(): ReactNode {
  const { t } = useTranslation();
  const { locations, activeLocationId, setActiveLocationId } = useLocations();
  if (locations.length === 0) return null;
  const active = locations.find((l) => l.id === activeLocationId);
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="inline-flex h-9 max-w-[12rem] cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 text-sm font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
        <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <span className="truncate">{active?.name ?? t('nav.allBranches')}</span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="start"
          sideOffset={6}
          className="z-50 min-w-[12rem] rounded-xl border border-border bg-surface p-1 shadow-pop data-[state=open]:animate-fade-in"
        >
          <Dropdown.Item
            onSelect={() => setActiveLocationId(null)}
            className="cursor-pointer rounded-lg px-2.5 py-2 text-sm text-fg outline-none data-[highlighted]:bg-surface-2"
          >
            {t('nav.allBranches')}
          </Dropdown.Item>
          {locations.map((l) => (
            <Dropdown.Item
              key={l.id}
              onSelect={() => setActiveLocationId(l.id)}
              className="cursor-pointer rounded-lg px-2.5 py-2 text-sm text-fg outline-none data-[highlighted]:bg-surface-2"
            >
              {l.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

function UserMenu(): ReactNode {
  const { t } = useTranslation();
  const { claims, signOut } = useAuth();
  const role = claims?.app_role ?? 'member';
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-border pl-1.5 pr-2.5 text-sm font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/15 text-primary">
          <UserRound className="h-4 w-4" aria-hidden />
        </span>
        <span className="hidden sm:inline">{t(`staff.roles.${role}`)}</span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[12rem] rounded-xl border border-border bg-surface p-1 shadow-pop data-[state=open]:animate-fade-in"
        >
          <div className="px-2.5 py-2">
            <p className="text-sm font-semibold text-fg">{t(`staff.roles.${role}`)}</p>
            <p className="truncate text-xs text-muted">
              {t('nav.tenant')} {claims?.tenant_id?.slice(0, 8) ?? '—'}
            </p>
          </div>
          <Dropdown.Separator className="my-1 h-px bg-border" />
          <Dropdown.Item
            onSelect={() => void signOut()}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-danger outline-none data-[highlighted]:bg-danger/10"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t('auth.signOut')}
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

function MemberAppLink(): ReactNode {
  // Leaves the admin SPA for the member-app phone viewer served at `/app/` (sibling path).
  return (
    <a
      href="/app/"
      className="inline-flex h-9 items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      <Smartphone className="h-4 w-4 shrink-0" aria-hidden />
      <span className="hidden sm:inline">See User App</span>
    </a>
  );
}

function ThemeToggle(): ReactNode {
  const { mode, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      aria-label="Toggle theme"
      className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg border border-border text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      {mode === 'dark' ? (
        <Sun className="h-4 w-4" aria-hidden />
      ) : (
        <Moon className="h-4 w-4" aria-hidden />
      )}
    </button>
  );
}

export function AppShell(): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const items = navFor(claims?.app_role);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-surface lg:flex">
        <Brand />
        <SidebarNav items={items} />
        <div className="border-t border-border p-3">
          <StatusPill tone="success" label={t('nav.systemOk')} status="active" />
        </div>
      </aside>

      {/* Mobile off-canvas drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-border bg-surface animate-slide-in-right">
            <div className="flex items-center justify-between pr-2">
              <Brand />
              <button
                aria-label={t('common.close')}
                onClick={() => setMobileOpen(false)}
                className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <SidebarNav items={items} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-surface/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
          <button
            aria-label={t('common.actions')}
            onClick={() => setMobileOpen(true)}
            className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
          <div className="hidden items-center gap-2 sm:flex">
            <LocationSwitcher />
          </div>
          <div className="flex-1" />
          <MemberAppLink />
          <ThemeToggle />
          <LocaleSwitcher />
          <UserMenu />
        </header>

        <main key={location.pathname} className="flex-1 animate-fade-in px-4 py-5 sm:px-6 sm:py-6">
          <div className="mx-auto w-full max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster />
    </div>
  );
}
